import { useEffect, useState } from 'react';
import { MapPin, Building2 } from 'lucide-react';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { fetchInviteCities, fetchInviteStates } from '../api/use-public-invite';

interface PublicInviteStateCityFieldsProps {
  idPassbook: string;
  state: string;
  city: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
}

/** Estado → Cidade em cascata (vipcell/nathansfamous/teashop) — legado `GET /state/carregar-estados[/:estado]`. */
export function PublicInviteStateCityFields({
  idPassbook,
  state,
  city,
  onStateChange,
  onCityChange,
}: PublicInviteStateCityFieldsProps) {
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  useEffect(() => {
    fetchInviteStates(idPassbook)
      .then((list) => setStates(list.map((s) => s.acronym)))
      .catch(() => setStates([]));
  }, [idPassbook]);

  useEffect(() => {
    if (!state) {
      setCities([]);
      return;
    }
    let cancelled = false;
    fetchInviteCities(idPassbook, state)
      .then((list) => {
        if (!cancelled) setCities(list);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [idPassbook, state]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label>
          Estado <span className="text-destructive">*</span>
        </Label>
        <Select
          value={state || undefined}
          onValueChange={(v) => {
            onStateChange(v);
            onCityChange('');
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione o estado" />
          </SelectTrigger>
          <SelectContent>
            {states.map((acronym) => (
              <SelectItem key={acronym} value={acronym}>
                <span className="inline-flex items-center gap-2">
                  <MapPin className="size-3.5 text-muted-foreground" />
                  {acronym}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>
          Cidade <span className="text-destructive">*</span>
        </Label>
        <Select value={city || undefined} onValueChange={onCityChange} disabled={!state}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione a cidade" />
          </SelectTrigger>
          <SelectContent>
            {cities.map((name) => (
              <SelectItem key={name} value={name}>
                <span className="inline-flex items-center gap-2">
                  <Building2 className="size-3.5 text-muted-foreground" />
                  {name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
